import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type DocumentSequenceResource = components['schemas']['DocumentSequenceResource']
export type ResetFrequency = components['schemas']['ResetFrequency']
export type SequenceUpdateInput = {
  prefix?: string
  padding?: number
  reset_frequency?: ResetFrequency
}

function key(groupId: string, companyId: string) {
  return ['platform', 'groups', groupId, 'companies', companyId, 'sequences'] as const
}

function bodyOrThrow<T>(body: { data: T } | undefined, endpoint: string): T {
  if (!body) throw new Error(`${endpoint} returned no body`)
  return body.data
}

export function useCompanySequences(groupId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: key(groupId ?? '', companyId ?? ''),
    enabled: Boolean(groupId && companyId),
    queryFn: async (): Promise<DocumentSequenceResource[]> => {
      const result = await api.GET(
        '/platform/groups/{group}/companies/{company}/document-sequences',
        { params: { path: { group: groupId ?? '', company: companyId ?? '' } } },
      )
      return bodyOrThrow(
        result.data,
        'GET /platform/groups/{group}/companies/{company}/document-sequences',
      )
    },
  })
}

export function useUpdateSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      sequenceId,
      input,
    }: {
      groupId: string
      companyId: string
      sequenceId: string
      input: SequenceUpdateInput
    }): Promise<DocumentSequenceResource> => {
      const result = await api.PATCH(
        '/platform/groups/{group}/companies/{company}/document-sequences/{sequence}',
        {
          params: { path: { group: groupId, company: companyId, sequence: sequenceId } },
          body: input,
        },
      )
      return bodyOrThrow(
        result.data,
        'PATCH /platform/groups/{group}/companies/{company}/document-sequences/{sequence}',
      )
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: key(variables.groupId, variables.companyId) })
    },
  })
}

export function usePreviewSequence(
  groupId: string | null,
  companyId: string | null,
  type: string | null,
) {
  return useQuery({
    queryKey: [...key(groupId ?? '', companyId ?? ''), 'preview', type] as const,
    enabled: Boolean(groupId && companyId && type),
    queryFn: async (): Promise<{ document_type: string; preview: string }> => {
      const result = await api.GET(
        '/platform/groups/{group}/companies/{company}/document-sequences/{type}/preview',
        {
          params: { path: { group: groupId ?? '', company: companyId ?? '', type: type ?? '' } },
        },
      )
      return bodyOrThrow(
        result.data,
        'GET /platform/groups/{group}/companies/{company}/document-sequences/{type}/preview',
      )
    },
  })
}
